import { Axios } from "api/axios";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import React, { Fragment, useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Container, Form, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  exportSalesOrderProductCSV,
  fetchAllProductSalesOrders,
  fetchSalesOrderServiceByType
} from "slices/sales_order/thunk";
// import { OrderAndSalesAxios } from "../../../api/orderandsalesaxios";
import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import ExportButton from "Common/Components/Buttons/ExportButton";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import OrderTableContainer from "Common/Tables/OrderFiltersTable/OrderTableContainer";
import {
  defaultInnerStyle,
} from "Common/Components/PageContainer/PageContainer";
import DynamicTabs from "components/DynamicTabs";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { useResetTableOnTabChange } from "CustomHooks/useResetTableOnTabChange";
import { ClipboardList, FileText, Timer, Truck } from "lucide-react";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { Add } from "@mui/icons-material";
import { MecordColors } from "Constants/Color";
import { hideTagsSearchListingSection } from "../../utils/tagsSearchEmpty";

interface MasterListPageProps {
  title: string;
  addButtonLabel: string;
  addRoute: string;
  fetchThunk: any; // thunk for fetching data
  exportThunk: any; // thunk for exporting data
  columns: any[];
  statusToggleUrl: string;
  activeTab?: string; // current active tab
  activeType?: string | undefined; // product or services
  // API url for status toggle
  handleDelete?: any;
  onDeleteComplete?: () => void; // Optional callback for when delete completes
  reloadFlag?: number; // Flag to trigger data reload
  /** Tags-search: keep user on /tags-search/:tags while switching Order Log / Draft */
  onTagsSearchTabNavigate?: (tabValue: string) => void;
  noWrapper?: boolean;
  forceShowOnTagsSearch?: boolean;
}

const MasterListPage: React.FC<MasterListPageProps> = ({
  title,
  addButtonLabel,
  addRoute,
  // fetchThunk,
  // exportThunk,
  columns,
  statusToggleUrl,
  activeTab,
  activeType,
  handleDelete,
  onDeleteComplete,
  reloadFlag,
  onTagsSearchTabNavigate,
  noWrapper = false,
  forceShowOnTagsSearch = false,
}) => {
  document.title = "Sales Order";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { tags: tagsParam } = useParams<{ tags?: string }>();
  const tagFromUrl = React.useMemo(() => {
    if (!tagsParam) return "";
    try {
      return decodeURIComponent(tagsParam).trim();
    } catch {
      return tagsParam.trim();
    }
  }, [tagsParam]);
  // const selectData = createSelector(
  //   (state: any) => state.SalesOrder,
  //   (salesOrder) => ({
  //     list: salesOrder.salesOrderList,
  //     rowCount: salesOrder.rowCount,
  //     loading: salesOrder.loading,
  //     error: salesOrder.error,
  //   })
  // );

  // const { list, rowCount, loading, error } = useSelector(selectData);
  // console.log("List Data: line 46 in SalesOrder", list);

  // Statis Dummy Data
  const statuses = [
    {
      label: "Total Sales Orders",
      icon: <FileText size={18} />, // 📄 Represents all SO documents
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      count: 120,
      subLabel: "All sales orders created",
    },
    {
      label: "Active Orders",
      icon: <ClipboardList size={18} />, // 📝 Active / processing orders
      bgColor: "bg-red-50",
      textColor: "text-red-600",
      count: 35,
      subLabel: "Orders currently in process",
    },
    {
      label: "Delivered Orders",
      icon: <Truck size={18} />, // 🚚 Delivered shipments
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-600",
      count: 50,
      subLabel: "Completed deliveries",
    },
    {
      label: "Pending Delivery",
      icon: <Timer size={18} />, // ⏳ Waiting for dispatch/delivery
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      count: 82,
      subLabel: "Awaiting delivery",
    },
  ];

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.sales_order?.app_name,
    app_modules?.sales_order?.modules?.product
  );

  // sales_order_log || draft
  const tableState = usePreservedTableState({
    key: TableKeys.SALES_ORDER.PRODUCT,
    tabString: activeTab,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;
  const effectiveSearch = tagFromUrl || search;

  useResetTableOnTabChange(TableKeys.SALES_ORDER.PRODUCT, activeTab, resetState);



  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<any[]>([]);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [tableInstance, setTableInstance] = useState<any>();
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Add refresh trigger state
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";


  // Fetch list - memoized to prevent infinite loops
  const handleFetchSalesOrderData = React.useCallback(async () => {
    setLoading(true);
    try {
      let resData; // Type the response structure
      // console.log("activeTab in line 142", activeTab)
      if (activeType === "product") {

        resData = await dispatch(
          fetchAllProductSalesOrders({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search: effectiveSearch,
            ...(tagFromUrl ? { type: "tag" } : {}),
            filters: filterValue,
            stage: activeTab === "sales_order_log" ? "orders_log" : (activeTab || ""),
          })
        ).unwrap();
      } else {
        resData = await dispatch(
          fetchSalesOrderServiceByType({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search: effectiveSearch,
            ...(tagFromUrl ? { type: "tag" } : {}),
            filters: filterValue,
            stage: activeTab === "sales_order_log" ? "orders_log" : (activeTab || ""),
          })
        ).unwrap();
      }
      // console.log("resData in line 132 quotation: ", resData);

      // 💡 KEY CHANGE: The data is already in the expected shape, so we set it directly.
      // console.log("resData in line 172", resData)
      // Backend response structure: { status, success, message, count, data: [...] }
      const responseData = resData?.data?.data || resData?.data || resData;
      setData(Array.isArray(responseData) ? responseData : []);
      setTotal(resData?.data?.count || resData?.count || 0);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, activeType, pageIndex, pageSize, sortField, sortDirection, effectiveSearch, filterValue, activeTab]);

  // The issue: handleDelete is a function reference that doesn't change when called.
  // When you call a function, its reference doesn't change - only when it's redefined.
  // Solution: Use a refreshTrigger state that increments to force re-fetch.
  // We'll expose a refresh function that parent can call, OR wrap handleDelete.

  // Create a wrapped version of handleDelete that triggers refresh after delete
  // This wrapped version should be used by the parent instead of the original
  const wrappedHandleDelete = React.useCallback(async (...args: any[]) => {
    if (handleDelete) {
      try {
        await handleDelete(...args);
        // Trigger refresh after successful delete by incrementing refreshTrigger
        setRefreshTrigger(prev => prev + 1);
        if (onDeleteComplete) {
          onDeleteComplete();
        }
      } catch (error) {
        // Error is already handled in handleDelete, just log here
        // console.error("Delete operation failed:", error);
        throw error; // Re-throw so parent can handle it if needed
      }
    }
  }, [handleDelete, onDeleteComplete]);

  // Expose refresh function via useImperativeHandle or just use the wrapped version
  // For now, we'll make wrappedHandleDelete available, but parent needs to use it
  React.useEffect(() => {
    // If handleDelete prop reference changes, it means parent recreated it
    // This won't help detect when it's called, but it's a fallback
  }, [handleDelete]);

  useEffect(() => {
    handleFetchSalesOrderData();
  }, [
    dispatch,
    search,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    filterValue,
    activeTab,
    refreshTrigger, // Use refreshTrigger instead of handleDelete
    reloadFlag, // Watch reloadFlag to trigger refresh
  ]);


  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    if (!tagFromUrl) return;
    setSearchInput(tagFromUrl);
    setSearch(tagFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagFromUrl]);

  // Status toggle
  const handleStatusToggle = async (item: any) => {
    try {
      const updatedStatus = !item.is_active;
      const payload = { is_active: updatedStatus };
      setIsStatusUpdateLoading(true);

      await Axios.patch(`${statusToggleUrl}/${item.id}/update/`, payload);

      toast.success(
        `${title} ${updatedStatus ? "activated" : "deactivated"} successfully`
      );

      // dispatch(
      //   fetchThunk({
      //     page: pageIndex + 1,
      //     pageSize,
      //     sort: sortField,
      //     order: sortField ? sortDirection : "",
      //     search,
      //     filters: filterValue,
      //   })
      // );
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  // Export handler
  const handleExport = async (): Promise<{ blob: Blob; filename: string }> => {
    return await exportSalesOrderProductCSV({
      search: effectiveSearch,
      filters: filterValue,
      stage: activeTab === "sales_order_log" ? "orders_log" : (activeTab || ""),
    });
    //   link.click();
    //   link.remove();
    //   window.URL.revokeObjectURL(url);
    // } catch (error) {
    //   toast.error("Failed to export CSV");
    // }
    toast.info("Export functionality is not implemented yet!");
    throw new Error("Export not implemented");
  };

  function handleChange(e: any): void {
    setSearchInput(e.target.value);
  }

  const sectionContent = (
    <>
        <Row className="align-items-center g-2">
          <Col lg={3} className="me-auto">
            <h6 className="card-title mb-0">{title}</h6>
          </Col>
  
        </Row>

        <div className="d-flex align-items-center justify-content-between mt-1">
          <DynamicTabs
            activeTab={activeTab}
            activeType={activeType}
            basePath="sales_order"
            onTabChange={onTagsSearchTabNavigate}
            tabs={[
              { value: "sales_order_log", label: "Order Log" },
              { value: "draft", label: "Draft" },
            ]}
          />
        </div>

     

        <div className="mt-4">
          {noWrapper ? (
            <LoaderErrorWrapper
              loading={loading || isStatusUpdateLoading}
              error={error}
            >
              <MasterTableContainer
                columns={columns}
                data={data}
                totalRecords={total}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder={`Search ${title.toLowerCase()}...`}
                manualSorting={true}
                colValue={setFilterValue}
                onTableReady={setTableInstance}
              />
            </LoaderErrorWrapper>
          ) : (
            <Card className="mt-3">
              <CardBody>
                <LoaderErrorWrapper
                  loading={loading || isStatusUpdateLoading}
                  error={error}
                >
                  <MasterTableContainer
                    columns={columns}
                    data={data}
                    totalRecords={total}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    columnFilters={columnFilters}
                    setColumnFilters={setColumnFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    isBordered={false}
                    isPagination={true}
                    isGlobalFilter={false}
                    SearchPlaceholder={`Search ${title.toLowerCase()}...`}
                    manualSorting={true}
                    colValue={setFilterValue}
                    onTableReady={setTableInstance}
                  />
                </LoaderErrorWrapper>
              </CardBody>
            </Card>
          )}
        </div>
    </>
  );

  if (
    hideTagsSearchListingSection(
      noWrapper,
      loading || isStatusUpdateLoading,
      total,
      data?.length ?? 0,
      error,
    ) &&
    !forceShowOnTagsSearch
  ) {
    return null;
  }

  if (noWrapper) {
    return <div className="mb-4">{sectionContent}</div>;
  }

  return (
    <div className="mb page-content px-4">
      <Row className="mb-3">
        <AnalyticsCards statuses={statuses} />
      </Row>

      <div className="px-4" style={defaultInnerStyle}>
        {sectionContent}
      </div>
    </div>
  );
};

export default MasterListPage;
